import type { ComboboxItem, SelectProps } from '@mantine/core'
import { Combobox } from '@mantine/core'
import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { ExternalVendorIcons } from 'components/ExternalVendorIcons/ExternalVendorIcons'
import { LabelWithExternalVendorIcons } from 'components/LabelWithExternalVendorIcons/LabelWithExternalVendorIcons'
import { getExternalVendorIconItems } from 'utils/getExternalVendorIconItems'
import { getExternalVendorIconsWidth } from 'utils/getExternalVendorIconsWidth'

export type TSelectItemWithExternalVendors = ComboboxItem & {
  externalVendorNames?: SharedTypes.IrrigationScheduler.EIrrigationVendorName[]
}

const ICON_SIZE = 16
const CHEVRON_WIDTH = 28
const ICONS_TO_CHEVRON_GAP = 6

export const makeExternalVendorSelectProps = ({
  data,
  value,
}: {
  data: { items: TSelectItemWithExternalVendors[] }[]
  value: string | null | undefined
}): {
  selectProps: Pick<SelectProps, 'renderOption' | 'rightSection' | 'rightSectionWidth'>
  inputPaddingRight: number
} => {
  const externalVendorNamesByValue = Object.fromEntries(
    data.flatMap((group) =>
      group.items.map((item) => [
        item.value,
        getExternalVendorIconItems(item.externalVendorNames).map(({ vendorName }) => vendorName),
      ]),
    ),
  )

  if (!Object.values(externalVendorNamesByValue).some((externalVendorNames) => externalVendorNames.length)) {
    return { selectProps: {}, inputPaddingRight: CHEVRON_WIDTH }
  }

  const selectedExternalVendorNames = value ? (externalVendorNamesByValue[value] ?? []) : []

  const renderOption: SelectProps['renderOption'] = ({ option }) => (
    <LabelWithExternalVendorIcons
      label={option.label}
      externalVendorNames={externalVendorNamesByValue[option.value]}
      iconSize={ICON_SIZE}
    />
  )

  if (selectedExternalVendorNames.length === 0) {
    return { selectProps: { renderOption }, inputPaddingRight: CHEVRON_WIDTH }
  }

  const inputPaddingRight =
    getExternalVendorIconsWidth({ count: selectedExternalVendorNames.length, size: ICON_SIZE }) +
    ICONS_TO_CHEVRON_GAP +
    CHEVRON_WIDTH

  return {
    selectProps: {
      renderOption,
      rightSection: (
        <span css={{ display: 'flex', alignItems: 'center', gap: ICONS_TO_CHEVRON_GAP }}>
          <ExternalVendorIcons externalVendorNames={selectedExternalVendorNames} size={ICON_SIZE} />
          <Combobox.Chevron size="xs" />
        </span>
      ),
      rightSectionWidth: inputPaddingRight,
    },
    inputPaddingRight,
  }
}
